import type { SuperadminUser } from '../types';

type ToastFn = (message: string, options?: { type?: string }) => unknown;

interface RefreshDeps {
  refreshUsers: () => Promise<SuperadminUser[]>;
  onUsersUpdated?: (users: SuperadminUser[]) => void;
}

export interface CreditAdjustmentDeps extends RefreshDeps {
  getSelectedUser: () => SuperadminUser | null;
  getAdminName: () => string;
  getCreditDelta: () => number;
  getNote: () => string;
  buildEndpoint: (userId: string) => string;
  ensureAccessToken: () => Promise<string | null>;
  renderUserCreditInfo: (user: SuperadminUser | null) => void;
  closeModal: () => void;
  showToast: ToastFn;
  fetchImpl?: typeof fetch;
}

export interface SubscriptionExtensionDeps extends RefreshDeps {
  getSelectedUser: () => SuperadminUser | null;
  getAdminName: () => string;
  getMonths: () => number;
  getDays: () => number;
  getNote: () => string;
  buildEndpoint: (userId: string) => string;
  ensureAccessToken: () => Promise<string | null>;
  renderUserCreditInfo: (user: SuperadminUser | null) => void;
  closeModal: () => void;
  showToast: ToastFn;
  fetchImpl?: typeof fetch;
}

const DEFAULT_ERROR = 'An error occurred while processing the request.';

export function createHandleCreditAdjustmentSubmit({
  getSelectedUser,
  getAdminName,
  getCreditDelta,
  getNote,
  buildEndpoint,
  ensureAccessToken,
  refreshUsers,
  onUsersUpdated,
  renderUserCreditInfo,
  closeModal,
  showToast,
  fetchImpl,
}: CreditAdjustmentDeps) {
  return async (ev: Event): Promise<void> => {
    ev.preventDefault();
    const userData = getSelectedUser();
    if (!userData) {
      showToast('No user selected.', { type: 'error' });
      closeModal();
      return;
    }
    const userId = userData.userId || userData.id;
    if (!userId) {
      showToast('Invalid user ID.', { type: 'error' });
      closeModal();
      return;
    }
    const delta = getCreditDelta();
    const note = getNote();
    if (isNaN(delta) || delta < -10000 || delta > 10000) {
      showToast('Credit delta must be between -10000 and +10000.', { type: 'error' });
      return;
    }
    if (!note || note.length < 1 || note.length > 500) {
      showToast('Note is required (1-500 characters).', { type: 'error' });
      return;
    }
    try {
      const token = await ensureAccessToken();
      if (!token) {
        showToast('Authentication required. Please sign in.', { type: 'error' });
        closeModal();
        return;
      }
      const endpoint = buildEndpoint(userId);
      const response = await (fetchImpl || fetch)(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delta, note }),
      });
      if (!response.ok) {
        showToast(`Failed to adjust credits: ${response.status}`, { type: 'error' });
        return;
      }
      await response.json().catch(() => undefined);
      const adminName = getAdminName();
      const userName = userData.displayName || userData.name || userData.email || 'User';
      const actionLabel = delta > 0 ? 'added' : 'subtracted';
      const absValue = Math.abs(delta);
      showToast(
        `${adminName} ${actionLabel} ${absValue} credits for ${userName}. Note: "${note}"`,
        {
          type: 'success',
        }
      );
      closeModal();
      const users = await refreshUsers();
      onUsersUpdated?.(users);
      const refreshedUser = users.find(u => (u.userId || u.id) === userId) || null;
      renderUserCreditInfo(refreshedUser);
    } catch (err) {
      showToast(DEFAULT_ERROR, { type: 'error' });
    }
  };
}

export function createHandleSubscriptionExtensionSubmit({
  getSelectedUser,
  getAdminName,
  getMonths,
  getDays,
  getNote,
  buildEndpoint,
  ensureAccessToken,
  refreshUsers,
  onUsersUpdated,
  renderUserCreditInfo,
  closeModal,
  showToast,
  fetchImpl,
}: SubscriptionExtensionDeps) {
  return async (ev: Event): Promise<void> => {
    ev.preventDefault();
    const userData = getSelectedUser();
    if (!userData) {
      showToast('No user selected.', { type: 'error' });
      closeModal();
      return;
    }
    const userId = userData.userId || userData.id;
    if (!userId) {
      showToast('Invalid user ID.', { type: 'error' });
      closeModal();
      return;
    }
    const months = getMonths();
    const days = getDays();
    const note = getNote();

    if (isNaN(months) || months < 0 || months > 120) {
      showToast('Months must be between 0 and 120.', { type: 'error' });
      return;
    }
    if (isNaN(days) || days < 0 || days > 3650) {
      showToast('Days must be between 0 and 3650.', { type: 'error' });
      return;
    }
    if (months === 0 && days === 0) {
      return;
    }
    if (!note || note.length < 1 || note.length > 500) {
      showToast('Note is required (1-500 characters).', { type: 'error' });
      return;
    }

    try {
      const token = await ensureAccessToken();
      if (!token) {
        showToast('Authentication required. Please sign in.', { type: 'error' });
        closeModal();
        return;
      }

      const body: { note: string; months?: number; days?: number } = { note };
      if (months > 0) body.months = months;
      if (days > 0) body.days = days;

      const endpoint = buildEndpoint(userId);
      const response = await (fetchImpl || fetch)(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        showToast(`Failed to extend subscription: ${response.status}`, { type: 'error' });
        return;
      }

      await response.json().catch(() => undefined);
      const adminName = getAdminName();
      const userName = userData.displayName || userData.name || userData.email || 'User';
      const durationParts = [] as string[];
      if (months > 0) durationParts.push(`${months} month${months !== 1 ? 's' : ''}`);
      if (days > 0) durationParts.push(`${days} day${days !== 1 ? 's' : ''}`);
      const durationStr = durationParts.join(' and ');
      showToast(
        `${adminName} extended subscription for ${userName} by ${durationStr}. Note: "${note}"`,
        {
          type: 'success',
        }
      );
      closeModal();
      const users = await refreshUsers();
      onUsersUpdated?.(users);
      const refreshedUser = users.find(u => (u.userId || u.id) === userId) || null;
      renderUserCreditInfo(refreshedUser);
    } catch (err) {
      showToast(DEFAULT_ERROR, { type: 'error' });
    }
  };
}
